import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Container, Grid, Typography } from '@mui/material'
import {
  createUsers,
  editUser,
  retrieveUserByID,
  sysAdminUsersResetAlertHandler,
  updateSelectedUserStore,
  updateTenantID,
} from '../../../../redux/features/users/sysAdmin/sysAdminUsersThunk'
import GoBackButton from '../../../../components/Buttons/GoBackButton.jsx'
import UserStepOne from './UserSteps/UserStepOne.jsx'
import UserStepTwo from './UserSteps/UserStepTwo.jsx'
import OfoqThemeBtn from '../../../../components/Buttons/OfoqThemeBtn.jsx'
import { sysAdminSelectUsers } from '../../../../redux/features/users/sysAdmin/sysAdminUsersSlice'
import OfoqIosSwitch from '../../../../components/Forms/OfoqIosSwitch.jsx'
import ConfirmationModalContainer from '../../../../components/Modals/ConfirmationModalContainer.jsx'
import FinishModalContainer from '../../../../components/Modals/FinishModalContainer.jsx'
import { userActiveStatesList } from '../../../../components/utils/buttonList'
import { isNotEmpty } from '../../../../utils/helpers'
import LoaderWithErrorHandler from '../../../../components/PageHelpers/LoaderWithErrorHandler.jsx'

const AddEditUser = () => {
  const sysAdminSelectUsersStore = useSelector(sysAdminSelectUsers)
  const { updatedUser } = sysAdminSelectUsersStore
  const { id } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const userActivateModalData = userActiveStatesList.activate
  const userDeactivateModalData = userActiveStatesList.deactivate

  const [activeStep, setActiveStep] = useState(1)
  const [validStepOne, setValidStepOne] = useState(false)
  const [validStepTwo, setValidStepTwo] = useState(true)
  const [activeUser, setActiveUser] = useState(updatedUser?.active || true)
  const [deactivateModalState, setDeactivateModalState] = useState(false)
  const [finishModalState, setFinishModalState] = useState(false)

  useEffect(() => {
    setActiveUser(updatedUser?.active)
    if (updatedUser?.customer_admin) {
      setValidStepTwo(false)
    } else if (isNotEmpty(updatedUser?.roles)) {
      setValidStepTwo(false)
    } else {
      setValidStepTwo(true)
    }
  }, [updatedUser])

  useEffect(() => {
    if (id) {
      dispatch(
        retrieveUserByID({
          queries: {
            id,
          },
        }),
      )
    }
  }, [])

  const stepHandler = (nextCheck) => {
    if (activeStep < 2) {
      setActiveStep(nextCheck ? activeStep + 1 : activeStep - 1)
    } else {
      setActiveStep(activeStep - 1)
    }
  }

  const confirmHandler = () => {
    const action = id
      ? editUser({ queries: { updatedUser } })
      : createUsers({ queries: { updatedUser } });

    dispatch(action).then((response) => {
      if (response.meta.requestStatus === 'fulfilled') {
        history.goBack();
      }
    });
  };

  const changeStatusHandler = () => {
    setDeactivateModalState(true)
  }

  const handelCloseModals = () => {
    setDeactivateModalState(false)
    setFinishModalState(false)
  }

  const cancelHandler = (cancel) => {
    dispatch(
      updateTenantID({
        queries: null,
      }),
    )

    dispatch(
      updateSelectedUserStore({
        queries: null,
      }),
    )
    if (cancel) {
      history.goBack()
    }
  }

  return (
    <>
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={styles.goBackWrapper}>
            <GoBackButton label={'Back to Users'} customBackLink='/users' andThisAction={cancelHandler} />
          </Grid>

          <Grid item xs={12} sx={styles.usersWrapper}>
            <Box sx={styles.usersSection}>
              {id === undefined ? (
                <Typography sx={styles.usersTitle}>{'Add New Customer User'}</Typography>
              ) : (
                <Typography sx={styles.usersTitle}>{'Edit Customer User'}</Typography>
              )}
            </Box>

            <Box sx={styles.actionButtonsContainer}>
              {id && activeStep === 1 && (
                <Box sx={styles.activeBtnContainer}>
                  <Typography variant='p' sx={styles.inactive}>
                    Inactive
                  </Typography>
                  <OfoqIosSwitch defaultValue={activeUser} onChangeHandler={changeStatusHandler}
                  customStyle={styles.iosSwitch} />
                  <Typography variant='p' sx={styles.active}>
                    Active
                  </Typography>
                </Box>
              )}

              {activeStep === 2 ? (
                <OfoqThemeBtn
                  onClick={() => stepHandler()}
                  name={'Back'}
                  cancelStyle
                  customStyle={styles.ButtonsContainer}
                />
              ) : (
                <OfoqThemeBtn
                  onClick={() => cancelHandler(true)}
                  name={'Cancel'}
                  cancelStyle
                  customStyle={styles.ButtonsContainer}
                />
              )}

              {activeStep === 1 && (
                <OfoqThemeBtn
                  onClick={() => stepHandler(true)}
                  name={'Next'}
                  dimmed={!validStepOne}
                  disabled={!validStepOne}
                  customStyle={styles.ButtonsContainer}
                />
              )}

              {activeStep === 2 && (
                <OfoqThemeBtn
                  onClick={() => confirmHandler()}
                  name={'Finish'}
                  dimmed={validStepTwo}
                  disabled={validStepTwo}
                  customStyle={styles.ButtonsContainer}
                />
              )}
            </Box>
            <br />
          </Grid>
        </Grid>

        <Grid container justify='space-between' alignItems='center'>
          <Grid item xs={6} lg={6}>
            <Typography sx={styles.infoTabsTitle}>1. Basic Information</Typography>
            <Box style={styles.activeStep}></Box>
          </Grid>
          <Grid item xs={6} lg={6}>
            <Typography sx={styles.infoTabsTitle}>2. Role Information</Typography>
            {activeStep >= 2 ? (
              <Box style={styles.activeStep}></Box>
            ) : (
              <Box style={styles.step}></Box>
            )}
          </Grid>

        </Grid>
      </Container>
      <Container maxWidth='xl' style={styles.dataContainer}>
        <Box sx={styles.activeStepBody}>
          {activeStep === 1 && <UserStepOne setValidStepOne={setValidStepOne} />}
          {activeStep === 2 && <UserStepTwo setValidStepTwo={setValidStepTwo} />}
        </Box>
      </Container>

      <ConfirmationModalContainer
        modalState={deactivateModalState}
        setModalState={setDeactivateModalState}
        setFinishModalState={setFinishModalState}
        confirmationData={activeUser ? userDeactivateModalData : userActivateModalData}
        confirmHandler={updateSelectedUserStore}
        confirmHandlerQueries={{
          ...updatedUser,
          active: !activeUser,
        }}
        customStyles={styles.confirmationModalContainer}
      />

      <FinishModalContainer
        modalTitle={!activeUser ? userDeactivateModalData.title : userActivateModalData.title}
        modalMessage={!activeUser ? userDeactivateModalData.finish : userActivateModalData.finish}
        modalState={finishModalState}
        finishHandler={handelCloseModals}
      />

      <LoaderWithErrorHandler
        store={sysAdminSelectUsersStore}
        resetAlertHandler={sysAdminUsersResetAlertHandler}
      />

    </>
  )
}

const styles = {
  goBackWrapper: {
    '&.MuiGrid-root': {
      paddingTop: { xs: '20px', sm: '0px' },
    },
  },
  usersWrapper: {
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    flexWrap: 'wrap',
    flexDirection: { xs: 'column', sm: 'row' },

    '&.MuiGrid-root': {
      paddingTop: { xs: '0px', sm: 'unset' },
    },

  },
  usersSection: {
    margin: { xs: '0px 0px 10px 0px', sm: 0 },
  },
  usersTitle: {
    color: '#171725',
    lineHeight: 1.36,
    letterSpacing: 0.1,
    fontSize: { xs: '20px', sm: '24px' },
    fontWeight: { xs: 500, sm: 900 },
  },
  actionButtonsContainer: {
    display: 'flex',
    width: '100%',
    marginTop: { xs: 0, sm: '-50px' },
    float: 'right',
    justifyContent: { xs: 'space-between', sm: 'flex-end' },
  },
  ButtonsContainer: {
    maxWidth: { xs: '70px', md: '150px' },
    height: { xs: '30px', sm: '40px' },
  },
  infoTabsTitle: {
    fontFamily: 'Avenir',
    fontSize: { xs: '12px', sm: '14px' },
    color: '#000000',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'block',
  },
  activeStep: {
    height: '5px',
    borderRadius: '2px',
    width: '99%',
    backgroundColor: '#29cc8b',
    marginTop: '12px',
    cursor: 'pointer',
  },
  dataContainer: {
    paddingLeft: { xs: '0px', sm: 'unset' },
    marginBottom: { xs: '20px', sm: 'unset' },
  },
  activeStepBody: {
    paddingTop: '3%',
    width: '100%',
    marginBottom: '20px',
  },
  step: {
    height: '5px',
    borderRadius: '2px',
    width: '99%',
    backgroundColor: '#eaebf1',
    marginTop: '12px',
    cursor: 'pointer',
  },
  activeBtnContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  inactive: {
    color: '#92929d',
    fontSize: { xs: '12px', sm: 'unset' },
  },
  active: {
    color: '#29cc8b',
    fontSize: { xs: '12px', sm: 'unset' },
  },
  iosSwitch: {
    '&.MuiSwitch-root': {
      margin: { xs: '0 8px', sm: 'unset' },
    },
  },

}

export default AddEditUser
